"use client";

import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useSpring, useTransform, useInView } from "framer-motion";
import { Link } from "@/i18n/navigation";
import { Icon } from "./Icon";
import { getNhcxData } from "../[locale]/nhcx/nhcx_data";

const getVisualTranslations = (locale: string) => {
  if (locale === "hi") {
    return {
      bundleId: "बंडल ID: NHCX-9821",
      docsCount: "102 दस्तावेज़",
      clinHistory: "क्लिनिकल इतिहास",
      labReports: "प्रयोगशाला रिपोर्ट",
      dischargeForm: "डिस्चार्ज फॉर्म",
      payerPol: "बीमाकर्ता · Pol #12X9",
      sla: "SLA: 2.4 घंटे",
      sanctioned: "स्वीकृत",
      requested: "अनुरोधित",
      approved: "स्वीकृत",
      autoDischarge: "ऑटो-डिस्चार्ज नोट",
      drSharma: "डॉ. शर्मा",
      eSigned: "ई-हस्ताक्षरित",
      finalBill: "अंतिम बिल",
      icuMgmt: "ICU प्रबंधन",
      pharmacy: "फार्मेसी",
      consult: "परामर्श",
      total: "कुल",
      icdEngine: "ICD-10 और टैरिफ इंजन",
      asthma: "अस्थमा (दमा)",
      nicotine: "निकोटीन इतिहास",
      icuTariff: "ICU टैरिफ",
      pass: "पास",
      riskAnalysis: "लाइव जोखिम विश्लेषण",
      reviewing: "समीक्षा जारी",
      lowRisk: "कम जोखिम",
      los: "अस्पताल में रहने की अवधि",
      expected: "अपेक्षित",
      tariffVar: "टैरिफ विचलन",
      priorAuth: "पूर्व प्राधिकार",
      match: "सटीक मिलान",
      finalPayout: "अंतिम भुगतान",
      insurer: "बीमाकर्ता",
      nhcx: "NHCX",
      hospital: "अस्पताल",
      tds: "TDS",
      settled: "भुगतान पूर्ण",
    };
  }
  return {
    bundleId: "Bundle ID: NHCX-9821",
    docsCount: "102 Docs",
    clinHistory: "Clinical History",
    labReports: "Lab Reports",
    dischargeForm: "Discharge Form",
    payerPol: "Payer · Pol #12X9",
    sla: "SLA: 2.4h",
    sanctioned: "SANCTIONED",
    requested: "Requested",
    approved: "Approved",
    autoDischarge: "Auto-Discharge Note",
    drSharma: "Dr. Sharma",
    eSigned: "E-Signed",
    finalBill: "FINAL BILL",
    icuMgmt: "ICU Mgmt",
    pharmacy: "Pharmacy",
    consult: "Consult",
    total: "TOTAL",
    icdEngine: "ICD-10 & Tariff Engine",
    asthma: "Asthma",
    nicotine: "Nicotine hist.",
    icuTariff: "ICU Tariff",
    pass: "PASS",
    riskAnalysis: "Live Risk Analysis",
    reviewing: "Reviewing",
    lowRisk: "LOW RISK",
    los: "Length of Stay",
    expected: "Expected",
    tariffVar: "Tariff Variance",
    priorAuth: "Prior Auth",
    match: "Match",
    finalPayout: "Final Payout",
    insurer: "Insurer",
    nhcx: "NHCX",
    hospital: "Hospital",
    tds: "TDS",
    settled: "Settled",
  };
};

/* ─── Micro visual components ─────────────────────────────────── */

function VisualBundled({ active, locale }: { active: boolean; locale: string }) {
  const vt = getVisualTranslations(locale);
  const docs = [
    { label: vt.clinHistory, delay: 0 },
    { label: vt.labReports, delay: 0.12 },
    { label: vt.dischargeForm, delay: 0.24 },
  ];
  return (
    <div className="nhcx-mini-visual" style={{ background: "#DFFAF8", border: "1px solid rgba(10,158,154,0.22)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#0A9E9A", textTransform: "uppercase", letterSpacing: "0.05em" }}>{vt.bundleId}</span>
        <motion.span
          style={{ fontSize: 10, fontWeight: 800, background: "rgba(10,158,154,0.18)", color: "#0A9E9A", padding: "2px 8px", borderRadius: 4 }}
          initial={{ opacity: 0 }}
          animate={active ? { opacity: 1 } : {}}
          transition={{ delay: 0.5 }}
        >
          {vt.docsCount}
        </motion.span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {docs.map((doc) => (
          <motion.div
            key={doc.label}
            initial={{ opacity: 0, x: -12 }}
            animate={active ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: doc.delay, type: "spring", stiffness: 280, damping: 20 }}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(10,158,154,0.18)" }}
          >
            <Icon name="file" width={12} height={12} style={{ color: "#0A9E9A", flexShrink: 0 }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: "#053C61" }}>{doc.label}</span>
            <motion.span
              initial={{ scale: 0 }}
              animate={active ? { scale: 1 } : {}}
              transition={{ delay: doc.delay + 0.3, type: "spring" }}
              style={{ marginLeft: "auto", color: "#2A9E5B", fontSize: 10, fontWeight: 800 }}
            >✓</motion.span>
          </motion.div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "center" }}>
        {["EMR", "LIS", "PACS"].map((src, i) => (
          <motion.div
            key={src}
            initial={{ opacity: 0, y: 6 }}
            animate={active ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 + i * 0.08 }}
            style={{ fontSize: 9, fontWeight: 800, background: "rgba(10,158,154,0.15)", color: "#0A9E9A", padding: "3px 8px", borderRadius: 4, border: "1px solid rgba(10,158,154,0.3)" }}
          >
            {src}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function VisualSanctioned({ active, locale }: { active: boolean; locale: string }) {
  const vt = getVisualTranslations(locale);
  const [amount, setAmount] = useState(0);
  useEffect(() => {
    if (!active) return;
    const target = 84000;
    const duration = 1000;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAmount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    const t = setTimeout(() => requestAnimationFrame(tick), 300);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <div className="nhcx-mini-visual" style={{ background: "#EAF7EE", border: "1px solid rgba(42,158,91,0.22)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#1A6033", textTransform: "uppercase" }}>{vt.payerPol}</span>
        <span style={{ fontSize: 10, background: "rgba(42,158,91,0.15)", color: "#1A6033", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>{vt.sla}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <div style={{ background: "#fff", border: "1px solid rgba(42,158,91,0.18)", borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ width: "60%", height: 4, background: "#e5e7eb", borderRadius: 2, marginBottom: 8 }} />
            <div style={{ width: "100%", height: 4, background: "#f3f4f6", borderRadius: 2, marginBottom: 6 }} />
            <div style={{ width: "80%", height: 4, background: "#f3f4f6", borderRadius: 2 }} />
          </div>
          <motion.div
            initial={{ scale: 4, opacity: 0, rotate: -12 }}
            animate={active ? { scale: 1, opacity: 1, rotate: -8 } : {}}
            transition={{ type: "spring", stiffness: 300, damping: 16, delay: 0.2 }}
            style={{
              position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
              border: "3px solid #2A9E5B", borderRadius: 6, padding: "6px 12px",
              color: "#2A9E5B", fontWeight: 900, fontSize: 14, textTransform: "uppercase",
              background: "rgba(255,255,255,0.96)", letterSpacing: "0.04em",
              boxShadow: "0 6px 16px rgba(42,158,91,0.18)", whiteSpace: "nowrap",
            }}
          >
            {vt.sanctioned}
          </motion.div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(42,158,91,0.18)", marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 2 }}>{vt.requested}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#9CA3AF", textDecoration: "line-through" }}>₹84,000</div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 8 }}
            animate={active ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.5 }}
            style={{ background: "#2A9E5B", padding: "10px 12px", borderRadius: 8, color: "#fff" }}
          >
            <div style={{ fontSize: 10, opacity: 0.85, marginBottom: 2 }}>{vt.approved}</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>₹{amount.toLocaleString("en-IN")}</div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function VisualDischarged({ active, locale }: { active: boolean; locale: string }) {
  const vt = getVisualTranslations(locale);
  const items = [
    { label: vt.icuMgmt, amt: "₹75,300", delay: 0.2 },
    { label: vt.pharmacy, amt: "₹8,200", delay: 0.4 },
    { label: vt.consult, amt: "₹500", delay: 0.6 },
  ];
  return (
    <div className="nhcx-mini-visual" style={{ background: "#FFF4DE", border: "1px solid rgba(184,133,0,0.22)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#B88500", textTransform: "uppercase" }}>{vt.autoDischarge}</span>
        <motion.div
          initial={{ opacity: 0 }}
          animate={active ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          style={{ display: "flex", gap: 6 }}
        >
          <span style={{ fontSize: 9, background: "rgba(184,133,0,0.15)", color: "#B88500", padding: "3px 6px", borderRadius: 4, fontWeight: 700 }}>{vt.drSharma}</span>
          <span style={{ fontSize: 9, background: "rgba(42,158,91,0.15)", color: "#2A9E5B", padding: "3px 6px", borderRadius: 4, fontWeight: 700 }}>{vt.eSigned}</span>
        </motion.div>
      </div>
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <div style={{ padding: "8px 12px", borderBottom: "1px dashed #e5e7eb", fontSize: 10, fontWeight: 800, color: "#6B7280", textAlign: "center" }}>{vt.finalBill}</div>
        <div style={{ padding: "8px 12px" }}>
          {items.map((item) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, x: -8 }}
              animate={active ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: item.delay }}
              style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 11, color: "#374151" }}
            >
              <span>{item.label}</span>
              <strong style={{ fontWeight: 600 }}>{item.amt}</strong>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0 }}
            animate={active ? { opacity: 1 } : {}}
            transition={{ delay: 0.85 }}
            style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, borderTop: "1px dashed #e5e7eb", fontSize: 12, fontWeight: 800, color: "#B88500", marginTop: 4 }}
          >
            <span>{vt.total}</span><span>₹84,000</span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function VisualVerified({ active, locale }: { active: boolean; locale: string }) {
  const vt = getVisualTranslations(locale);
  const codes = [
    { c: "J45.909", d: vt.asthma, delay: 0.1 },
    { c: "Z87.891", d: vt.nicotine, delay: 0.3 },
    { c: "7.2.01", d: vt.icuTariff, delay: 0.5 },
  ];
  return (
    <div className="nhcx-mini-visual" style={{ background: "#F6F2FF", border: "1px solid rgba(109,40,217,0.15)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#6D28D9", textTransform: "uppercase" }}>{vt.icdEngine}</span>
        <span style={{ fontSize: 10, background: "rgba(109,40,217,0.1)", color: "#6D28D9", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>NHCX v1.2</span>
      </div>
      {codes.map((item) => (
        <motion.div
          key={item.c}
          initial={{ opacity: 0, x: -8 }}
          animate={active ? { opacity: 1, x: 0 } : {}}
          transition={{ delay: item.delay }}
          style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(109,40,217,0.12)", marginBottom: 6 }}
        >
          <span style={{ background: "rgba(109,40,217,0.1)", color: "#6D28D9", padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{item.c}</span>
          <span style={{ fontSize: 11, color: "#4B5563", flex: 1, fontWeight: 500 }}>{item.d}</span>
          <motion.span initial={{ scale: 0 }} animate={active ? { scale: 1 } : {}} transition={{ delay: item.delay + 0.2, type: "spring" }} style={{ color: "#2A9E5B", fontSize: 12, fontWeight: 800 }}>✓</motion.span>
        </motion.div>
      ))}
      <motion.div
        initial={{ opacity: 0 }}
        animate={active ? { opacity: 1 } : {}}
        transition={{ delay: 0.9 }}
        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}
      >
        <div style={{ flex: 1, height: 6, background: "rgba(109,40,217,0.08)", borderRadius: 3, overflow: "hidden" }}>
          <motion.div initial={{ width: 0 }} animate={active ? { width: "100%" } : {}} transition={{ delay: 1, duration: 0.6 }} style={{ height: "100%", background: "#2A9E5B", borderRadius: 3 }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#2A9E5B" }}>{vt.pass}</span>
      </motion.div>
    </div>
  );
}

function VisualLowRisk({ active, locale }: { active: boolean; locale: string }) {
  const vt = getVisualTranslations(locale);
  const factors = [
    { l: vt.los, v: vt.expected, delay: 0.6 },
    { l: vt.tariffVar, v: "0.0%", delay: 0.75 },
    { l: vt.priorAuth, v: vt.match, delay: 0.9 },
  ];
  return (
    <div className="nhcx-mini-visual" style={{ background: "#FCE5EC", border: "1.5px solid rgba(204,43,88,0.22)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#CC2B58", textTransform: "uppercase" }}>{vt.riskAnalysis}</span>
        <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ repeat: Infinity, duration: 1.6 }} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 700, background: "rgba(204,43,88,0.12)", color: "#CC2B58", padding: "3px 8px", borderRadius: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#CC2B58", display: "inline-block" }} />
          {vt.reviewing}
        </motion.span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ position: "relative", width: 84, height: 46 }}>
            <svg width="84" height="46" viewBox="0 0 84 46" style={{ overflow: "visible", position: "absolute", top: 0, left: 0 }}>
              <path d="M 6 42 A 36 36 0 0 1 78 42" fill="none" stroke="rgba(204,43,88,0.12)" strokeWidth="10" strokeLinecap="round" />
              <motion.path
                d="M 6 42 A 36 36 0 0 1 78 42"
                fill="none" stroke="#2A9E5B" strokeWidth="10" strokeLinecap="round"
                strokeDasharray="114"
                initial={{ strokeDashoffset: 114 }}
                animate={active ? { strokeDashoffset: 114 - 114 * 0.04 * 3 } : {}}
                transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
              />
            </svg>
            <motion.div
              initial={{ rotate: -82 }}
              animate={active ? { rotate: -70 } : {}}
              transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
              style={{ position: "absolute", bottom: 4, left: "50%", width: 3, height: 26, background: "#053C61", borderRadius: 1.5, transformOrigin: "bottom center", marginLeft: -1.5, zIndex: 2 }}
            />
            <div style={{ position: "absolute", bottom: 1, left: "50%", width: 9, height: 9, background: "#053C61", borderRadius: "50%", transform: "translateX(-50%)", zIndex: 3 }} />
          </div>
          <motion.div initial={{ opacity: 0, y: 4 }} animate={active ? { opacity: 1, y: 0 } : {}} transition={{ delay: 1 }} style={{ textAlign: "center", marginTop: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#2A9E5B", lineHeight: 1 }}>4%</div>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#2A9E5B", letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 2 }}>{vt.lowRisk}</div>
          </motion.div>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
          {factors.map((f) => (
            <motion.div key={f.l} initial={{ opacity: 0, x: 10 }} animate={active ? { opacity: 1, x: 0 } : {}} transition={{ delay: f.delay }} style={{ display: "flex", justifyContent: "space-between", background: "#fff", padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(204,43,88,0.12)", fontSize: 11 }}>
              <span style={{ color: "#6B7280" }}>{f.l}</span>
              <span style={{ fontWeight: 700, color: "#053C61" }}>{f.v}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VisualSettled({ active, locale }: { active: boolean; locale: string }) {
  const vt = getVisualTranslations(locale);
  const [amount, setAmount] = useState(0);
  const [utr, setUtr] = useState("");
  const fullUtr = "UTR: N123898X";
  useEffect(() => {
    if (!active) return;
    const target = 84000;
    const dur = 1000;
    const start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / dur, 1);
      setAmount(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    const t1 = setTimeout(() => requestAnimationFrame(tick), 400);

    let idx = 0;
    const t2 = setInterval(() => {
      idx++;
      setUtr(fullUtr.slice(0, idx));
      if (idx >= fullUtr.length) clearInterval(t2);
    }, 60);
    return () => { clearTimeout(t1); clearInterval(t2); };
  }, [active]);

  return (
    <div className="nhcx-mini-visual" style={{ background: "#EAF7EE", border: "1px solid rgba(42,158,91,0.22)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#1A6033", textTransform: "uppercase" }}>{vt.finalPayout}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#1A6033", fontFamily: "monospace" }}>{utr || "UTR: ..."}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 8px", position: "relative", marginBottom: 16 }}>
        <div style={{ position: "absolute", top: "35%", left: 24, right: 24, height: 3, background: "rgba(42,158,91,0.15)", zIndex: 0 }} />
        <motion.div initial={{ scaleX: 0 }} animate={active ? { scaleX: 1 } : {}} transition={{ delay: 0.2, duration: 1.2, ease: "easeInOut" }} style={{ position: "absolute", top: "35%", left: 24, right: 24, height: 3, background: "#2A9E5B", boxShadow: "0 0 8px #2A9E5B", zIndex: 1, transformOrigin: "left" }} />
        {[vt.insurer, vt.nhcx, vt.hospital].map((label, i) => (
          <div key={label} style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <motion.div initial={{ scale: 0 }} animate={active ? { scale: 1 } : {}} transition={{ delay: 0.1 + i * 0.45, type: "spring", stiffness: 300, damping: 20 }} style={{ width: 32, height: 32, borderRadius: "50%", background: "#fff", border: "2px solid #2A9E5B", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 10px rgba(42,158,91,0.18)" }}>
              <Icon name={["shield", "network", "hospital"][i] as "shield" | "network" | "hospital"} width={14} height={14} style={{ color: "#1A6033" }} />
            </motion.div>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#1A6033", textTransform: "uppercase" }}>{label}</div>
          </div>
        ))}
      </div>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={active ? { opacity: 1, y: 0 } : {}} transition={{ delay: 1.6 }} style={{ display: "flex", justifyContent: "space-between", background: "#fff", border: "1px solid rgba(42,158,91,0.18)", borderRadius: 8, padding: "10px 12px" }}>
        {[{ label: vt.approved, val: `₹${amount.toLocaleString("en-IN")}`, color: "#053C61" }, { label: vt.tds, val: "₹0", color: "#053C61" }, { label: vt.settled, val: `₹${amount.toLocaleString("en-IN")}`, color: "#2A9E5B" }].map(({ label, val, color }) => (
          <div key={label} style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontSize: 9, color: "#9CA3AF", marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 800, color }}>{val}</div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

const VISUALS = [VisualBundled, VisualSanctioned, VisualDischarged, VisualVerified, VisualLowRisk, VisualSettled];

/* ─── Timeline Row Component ───────────────────────────────────── */
function TimelineRow({ step, index, locale }: { step: any; index: number; locale: string }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rowRef, { margin: "-45% 0px -45% 0px" });
  const passed = useInView(rowRef, { margin: "0px 0px -45% 0px" });

  const isActive = inView;
  const isReached = isActive || passed;
  const isLeft = step.side === "left";

  const Visual = VISUALS[index];

  const CardContent = () => (
    <div className="nhcx-step-card" style={{
      borderColor: isActive ? step.accentBorder : "rgba(11,23,51,0.07)",
      boxShadow: isActive ? `0 8px 32px rgba(0,0,0,0.08), 0 0 0 4px ${step.accentBg}` : "0 4px 20px rgba(11,23,51,0.05)",
      transition: "border-color 0.4s ease, box-shadow 0.4s ease",
    }}>
      <div className="nhcx-step-eyebrow">
        <span className="nhcx-step-num">{locale === "hi" ? `कदम` : `Step`} {step.step}</span>
        <AnimatePresence>
          {isActive && (
            <motion.span
              key="badge"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              transition={{ type: "spring", stiffness: 350, damping: 20 }}
              className="nhcx-step-badge"
              style={{ background: step.accentBg, color: step.accentColor }}
            >
              {step.statusWord}{step.statusSub && step.statusSub !== step.statusWord ? ` · ${step.statusSub}` : ""}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
      <div className="nhcx-step-title">{step.title}</div>
      <div className="nhcx-step-meta">{step.meta}</div>
      <Visual active={isReached} locale={locale} />
    </div>
  );

  return (
    <div className="nhcx-tl-row" ref={rowRef}>

      {/* ── Desktop: Left card column ── */}
      <div className="nhcx-tl-col left desktop-only">
        {isLeft && (
          <motion.div
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ margin: "-20% 0px -20% 0px", once: true }}
            transition={{ type: "tween", ease: "easeOut", duration: 0.55 }}
            style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
          >
            <CardContent />
          </motion.div>
        )}
      </div>

      {/* ── Center: Step status badge (desktop center / mobile absolute-left) ── */}
      <div className="nhcx-tl-node-wrap">
        <div
          className={`nhcx-timeline-badge ${isReached ? "active" : ""}`}
          style={{
            background: isReached ? step.accentColor : "#fff",
            borderColor: isReached ? step.accentColor : "var(--line-3)",
            color: isReached ? "#fff" : "var(--ink-4)",
            boxShadow: isActive ? `0 0 0 6px ${step.accentBorder}, 0 0 20px ${step.accentBorder}` : "none",
            transform: isActive ? "scale(1.05)" : "scale(1)",
          }}
        >
          {step.statusWord}
        </div>
      </div>

      {/* ── Desktop: Right card column ── */}
      <div className="nhcx-tl-col right desktop-only">
        {!isLeft && (
          <motion.div
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ margin: "-20% 0px -20% 0px", once: true }}
            transition={{ type: "tween", ease: "easeOut", duration: 0.55 }}
            style={{ width: "100%", display: "flex", justifyContent: "flex-start" }}
          >
            <CardContent />
          </motion.div>
        )}
      </div>

      {/* ── Mobile: full-width card column ── */}
      <div className="nhcx-tl-col mobile-only">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ margin: "-5% 0px -5% 0px", once: true }}
          transition={{ type: "tween", ease: "easeOut", duration: 0.45 }}
          style={{ width: "100%" }}
        >
          <CardContent />
        </motion.div>
      </div>

    </div>
  );
}

/* ─── Main section ────────────────────────────────────────────── */
export function NhcxLifecycleSection({ locale }: { locale: string }) {
  const t = getNhcxData(locale);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track scroll progress within this container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start center", "end center"],
  });

  // Smooth out the scroll value
  const scaleY = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  // Map scaleY to top % for the rotating traveller
  const travellerTop = useTransform(scaleY, [0, 1], ["0%", "100%"]);

  // Map scaleY to color transitions matching the step accent colors
  const travellerColor = useTransform(scaleY,
    [0, 0.2, 0.4, 0.6, 0.8, 1],
    ["#0A9E9A", "#2A9E5B", "#B88500", "#6D28D9", "#CC2B58", "#2A9E5B"]
  );
  const travellerShadow = useTransform(travellerColor, c => `0 0 12px ${c}`);

  return (
    <section className="section section--lg bg-dots">
      <div className="container">
        <div className="nhcx-lifecycle-wrap">
          {/* ── Header ── */}
          <div className="nhcx-lifecycle-head reveal">
            <span className="eyebrow">
              <span className="dot" /> {t.lifecycleEyebrow}
            </span>
            <h2 style={{ marginTop: 20 }}>
              {t.lifecycleTitle}
            </h2>
            <p style={{ marginTop: 18, color: "var(--ink-3)", maxWidth: 560, textAlign: "center", fontSize: 16 }}>
              {t.lifecycleDesc}
            </p>
            <Link href="https://claimiq.vigorus.ai" target="_blank" rel="noopener noreferrer" className="btn btn--ghost" style={{ marginTop: 28 }}>
              {t.howClaimIqWorks} <Icon name="arrow" />
            </Link>
          </div>

          {/* ── Cinematic Timeline ── */}
          <div className="nhcx-tl-layout" ref={containerRef}>
            {/* Background line */}
            <div className="nhcx-tl-line" />

            {/* Glowing fill synchronized with scroll */}
            <motion.div className="nhcx-tl-line-fill" style={{ scaleY }} />

            {/* Rotating traveller synchronized with scroll */}
            <motion.div className="nhcx-tl-traveller" style={{ top: travellerTop }}>
              <motion.div className="nhcx-tl-traveller-ring" style={{ borderColor: travellerColor }} />
              <motion.div className="nhcx-tl-traveller-core" style={{ borderColor: travellerColor, boxShadow: travellerShadow }} />
            </motion.div>

            {/* Steps */}
            {t.lifecycleSteps.map((step, i) => (
              <TimelineRow key={step.step} step={step} index={i} locale={locale} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
